// src/App.tsx
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { detect } from 'detect-browser';
import { MainAppReducer } from 'Types';
import { CustomCircularProgress } from 'Components/StyledComponents';
// import Welcome from './Containers/Welcome/Welcome';
import Welcome from 'Containers/TrialWelcome/Welcome';
import Tutorial from './Containers/Tutorial/Tutorial';
import Introduction from './Containers/Introduction/Introduction';
import Lesson from './Containers/Lesson/Lesson';
import Signin from './Containers/SignIn';
import Signup from './Containers/SignUp';
import Plan from './Containers/Plan/Plan';
import Roadmap from './Containers/Roadmap/Roadmap';
import Repertoire from './Containers/Repertoire/Repertoire';
import RepertoirePlay from './Containers/Repertoire/RepertoirePlay';
import Help from './Containers/Help/Help';
import HistoryOfPianoMusic from './Containers/HistoryOfPianoMusic/HistoryOfPianoMusic';
import Settings from './Containers/Settings/Settings';
import SoundOutput from 'Containers/TrialWelcome/SoundOutput';
import ErrorPage from './Containers/ErrorPage/ErrorPage';
import Verification from './Containers/Verification';
import Dashboard from 'Containers/Dashboard/Dashboard';
import { useState } from 'react';
import ProtectedRoute from 'Containers/ProtectedRoute';
// import { Amplify, Auth, Hub } from "aws-amplify";
// import { initializeMidiInput } from 'Utils/MidiInput';
import * as Tone from 'tone';
import { detectDevice } from 'Utils';
import logger from 'Utils/Logger';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { StatusBar, Style } from '@capacitor/status-bar';
import { App as CapApp } from '@capacitor/app';
import AuthWrapperProvider, { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import { oidcManager } from 'Utils/AuthService';
// import { useAuth } from "AuthContext";
import { decodeBase64ToAudioBuffer } from 'Utils/audioBuffers';
import { Browser } from '@capacitor/browser';
import { BrowserRouter, Route, Routes, Navigate, useLocation, useNavigate } from 'react-router-dom'; //Switch
import { DeviceCompatibilityBlockingScreen } from 'Containers/DeviceCompatibilityBlockingScreen';
import { MidiProvider } from 'Contexts/MidiContext';
import { RootState } from 'Reducers';
import { Store } from 'redux';
import ReactGA from 'react-ga4';
import { EventReducer } from 'Types/EventTypes';
import { NoteType } from '@museflow-ai/opensheetmusicdisplay';
import NoteLength from 'Components/Exercises/NoteLengthsByType';
import { isCapacitorNative, MIN_NUM_PHRASES } from 'Utils/Constants';
import { setupIAP } from 'iap/initPurchases';
import { awsRum } from 'Utils/AwsRum';
import { reportError } from 'Utils/reportError';
import MaintenancePage from 'Containers/Maintenance';
import { AppDispatch } from 'Reducers';
import OfflineModal from 'Components/OfflineModal';
import AnnouncementModalHost from 'Components/Notifications/AnnouncementModalHost';
import { Capacitor } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { eventQueue } from 'Models/EventQueue';
import * as appActions from 'Actions/app';
import * as lessonActions from 'Actions/lesson';
import * as eventActions from 'Actions/events';
import { RepertoirePlayProvider } from 'Contexts/RepertoirePlayContext';
import MuseFlowAudio, { isMuseFlowAudioPluginAvailable } from 'Utils/MuseFlowAudioPlugin';
import { FeatureFlag, useFeatureFlag } from 'Utils/FeatureFlags';
import {
  getDeviceClass,
  getOrientationLockForRoute,
  isFreeRotationEligibleScreen,
  isOrientationLockEligibleScreen,
  syncResponsiveDataAttributes,
} from 'Utils/Responsive';

/** Window extensions used for audio unlock/sampler (set by this app). */
interface MuseFlowAudioWindow extends Window {
  __audioNeedsRebuild?: boolean;
  __ensureSamplerReady?: (forceRebuild?: boolean) => Promise<void>;
  __museflowSampler?: import('tone').Sampler;
  __audioUnlocked?: boolean;
}

console.log('[App] Environment variables:', process.env);

const browser = detect();

const _region = process.env.REACT_APP_COGNITO_AWS_REGION;
const _cognitoUserPoolId = process.env.REACT_APP_COGNITO_USER_POOL_ID;
const _cognitoClientId = process.env.REACT_APP_COGNITO_CLIENT_ID;
// const cognitoGuestRoleArn = process.env.REACT_APP_COGNITO_GUEST_ROLE_ARN;
// let applicationVersion = process.env.REACT_APP_APPLICATION_VERSION;
const _oauthDomain = process.env.REACT_APP_OAUTH_REDIRECT_URL;
const ganayticsMeasurementId = process.env.REACT_APP_G_ANALYTICS_MEASUREMENT_ID;
const eventIntervalSeconds = process.env.REACT_APP_EVENT_INTERVAL_SECONDS;
if (!eventIntervalSeconds) {
  throw Error('REACT_APP_EVENT_INTERVAL_SECONDS is not set');
}
const eventIntervalSecondsInt = parseInt(eventIntervalSeconds);

if (process.env.REACT_APP_NODE_ENV == 'prd' && ganayticsMeasurementId) {
  ReactGA.initialize(ganayticsMeasurementId);
}

const _IS_NATIVE = Capacitor.isNativePlatform(); // Must be defined outside of component.

const NATIVE_AUDIO_ROUTE_PREFIXES = [
  '/lesson',
  '/tutorial',
  '/repertoire',
  '/repertoire-play',
  '/sound-output',
  '/exercises/note-length',
];

const isNativeAudioRoute = (pathname: string) =>
  NATIVE_AUDIO_ROUTE_PREFIXES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );

function AudioUnlocker() {
  const armedRef = useRef(false);
  const unlockedRef = useRef(false);
  const unlockInProgressRef = useRef(false);

  useEffect(() => {
    const removeHandlers: Array<() => void> = [];

    const unlockFromGesture = async () => {
      if (unlockedRef.current) return;
      // Prevent concurrent unlock attempts
      if (unlockInProgressRef.current) return;
      unlockInProgressRef.current = true;

      console.log('[AudioUnlock] Starting audio unlock...');

      try {
        // Helper to check if context is in a broken state that needs recreation
        const needsRecreation = (ctx: AudioContext | BaseAudioContext) => {
          const state = ctx?.state;
          // iOS can put context in "interrupted" state (not in Web Audio spec)
          // "closed" means context was destroyed
          // Also recreate if state is undefined or context doesn't exist
          return !ctx || state === 'closed' || state === 'interrupted';
        };

        // Helper to ensure context is running, with retries
        const ensureRunning = async (maxRetries = 3): Promise<boolean> => {
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            let raw = Tone.getContext().rawContext as AudioContext;

            // Check if we need to recreate the context
            if (needsRecreation(raw)) {
              console.log(`[AudioUnlock] Context in bad state (${raw?.state}), recreating...`);
              try {
                // Dispose old context if possible
                try {
                  Tone.getContext().dispose();
                } catch {
                  // intentionally empty
                }
              } catch {
                // intentionally empty
              }

              const newCtx = new Tone.Context({ latencyHint: 'interactive' });
              Tone.setContext(newCtx);
              raw = Tone.getContext().rawContext as AudioContext;
            }

            // Try to start and resume
            try {
              await Tone.start();
              await Tone.getContext().resume();

              // If still not running, try raw resume
              if (raw.state !== 'running') {
                await raw.resume();
              }

              // Small delay to let iOS process the resume
              await new Promise((resolve) => setTimeout(resolve, 50));

              // Re-check state after delay
              raw = Tone.getContext().rawContext as AudioContext;
              if (raw.state === 'running') {
                console.log('[AudioUnlock] Context now running');
                return true;
              }

              console.log(
                `[AudioUnlock] Attempt ${attempt + 1}/${maxRetries}: state is ${raw.state}`,
              );
            } catch (err) {
              console.warn(`[AudioUnlock] Resume attempt ${attempt + 1} failed:`, err);
            }

            // Wait before retry
            if (attempt < maxRetries - 1) {
              await new Promise((resolve) => setTimeout(resolve, 100));
            }
          }
          return false;
        };

        // Attempt to get audio running
        const success = await ensureRunning();

        if (!success) {
          // Last resort: create a completely fresh context
          console.log('[AudioUnlock] All retries failed, forcing fresh context creation');
          try {
            Tone.getContext().dispose();
          } catch {
            // intentionally empty
          }
          const freshCtx = new Tone.Context({ latencyHint: 'interactive' });
          Tone.setContext(freshCtx);
          await Tone.start();
          await Tone.getContext().resume();

          const finalRaw = Tone.getContext().rawContext as AudioContext;
          if (finalRaw.state !== 'running') {
            console.warn('[AudioUnlock] Could not start audio context, state:', finalRaw.state);
            // Mark that we need a rebuild on next attempt
            (window as MuseFlowAudioWindow).__audioNeedsRebuild = true;
            unlockInProgressRef.current = false;
            return;
          }
        }

        // Context is running, now ensure sampler is ready
        const needsRebuild = !!(window as MuseFlowAudioWindow).__audioNeedsRebuild;
        const ensure = (window as MuseFlowAudioWindow).__ensureSamplerReady;

        if (typeof ensure === 'function') {
          await ensure(needsRebuild);
        }
        (window as MuseFlowAudioWindow).__audioNeedsRebuild = false;

        // Kill any stuck voices
        try {
          (window as MuseFlowAudioWindow).__museflowSampler?.releaseAll?.();
        } catch {
          // intentionally empty
        }

        unlockedRef.current = true;
        (window as MuseFlowAudioWindow).__audioUnlocked = true;
        console.log('[AudioUnlock] Audio unlocked successfully');

        // Dispatch AudioReady event
        window.dispatchEvent(new CustomEvent('AudioReady', { detail: { ready: true } }));

        removeHandlers.forEach((fn) => fn());
      } catch (err) {
        reportError(err, 'audio.unlock');
        // Reset so we can retry on next gesture
        (window as MuseFlowAudioWindow).__audioNeedsRebuild = true;
      } finally {
        unlockInProgressRef.current = false;
      }
    };

    const armGesture = () => {
      if (armedRef.current || unlockedRef.current) return;
      armedRef.current = true;

      const opts: AddEventListenerOptions = { capture: true, passive: false };
      const handler = () => {
        unlockFromGesture();
      };

      window.addEventListener('pointerdown', handler, opts);
      window.addEventListener('touchstart', handler, opts);
      window.addEventListener('keydown', handler, { capture: true });

      removeHandlers.push(() => window.removeEventListener('pointerdown', handler, opts));
      removeHandlers.push(() => window.removeEventListener('touchstart', handler, opts));
      removeHandlers.push(() => window.removeEventListener('keydown', handler, { capture: true }));
    };

    // const onActive = () => {
    //   // mark audio dirty; force sampler rebuild on next gesture
    //   (window as MuseFlowAudioWindow).__audioNeedsRebuild = true;

    //   unlockedRef.current = false;
    //   (window as MuseFlowAudioWindow).__audioUnlocked = false;

    //   armedRef.current = false;
    //   armGesture();
    // };
    const onActive = () => {
      (window as MuseFlowAudioWindow).__audioNeedsRebuild = true;
      (window as MuseFlowAudioWindow).__audioUnlocked = false;
      window.dispatchEvent(new CustomEvent('AudioReady', { detail: { ready: false } }));

      unlockedRef.current = false;
      armedRef.current = false;
      armGesture();
    };

    armGesture();

    const vis = () => {
      if (!document.hidden) onActive();
    };
    document.addEventListener('visibilitychange', vis);

    let h1: PluginListenerHandle | undefined;
    let h2: PluginListenerHandle | undefined;
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) onActive();
    }).then((h) => (h1 = h));
    CapApp.addListener('resume', onActive).then((h) => (h2 = h));

    return () => {
      document.removeEventListener('visibilitychange', vis);
      h1?.remove?.();
      h2?.remove?.();
      removeHandlers.forEach((fn) => fn());
    };
  }, []);

  return null;
}

function _myFunc(_myvar: string) {
  // intentionally empty (kept for placeholder)
}

interface AppProps {
  // store: any; //Store<RootState>;
  // store: Store<RootState>;
}

const StartupRouteResolver: React.FC = () => {
  const auth = useAuthWrapperContext();
  const userData = useSelector((state: MainAppReducer) => state.mainAppReducer.userData);

  if (!auth.authInitialized || auth.isLoading || auth.oauthPending) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CustomCircularProgress />
      </Box>
    );
  }

  if (!auth.isAuthenticated) {
    return <Navigate to="/signin" replace />;
  }

  if (!userData) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CustomCircularProgress />
      </Box>
    );
  }

  const nextPath = userData.subscription_status === '' ? '/welcome' : '/roadmap';
  return <Navigate to={nextPath} replace />;
};

// function App({ store }) {
const App: React.FC<AppProps> = () => {
  // If the user hits refresh, on /lesson for example, we want to reload page with all of their current data
  // So we will want at top level here to set state from local store. (and then do whatever follow-up queries are necessary if there are any right off the bat)
  // State data should be: Current phrase (like s3 url), current lesson, current Tempo, ...
  const auth = useAuthWrapperContext();
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();

  const { currentAuthToken } = auth;
  // const { user, currentAuthToken } = useAuthWrapperContext();
  // const samplerRef = React.useRef<Tone.Sampler | null>(null);
  // const [samplerReady, setSamplerReady] = React.useState(false);
  // const sampler = React.useMemo(
  //   () => new Tone.Sampler({ urls: {}, release: 1 }).toDestination(),
  //   []
  // );

  // // Expose sampler to window for AudioUnlocker to access
  // React.useEffect(() => {
  //   (window as MuseFlowAudioWindow).__museflowSampler = sampler;
  //   return () => {
  //     delete (window as MuseFlowAudioWindow).__museflowSampler;
  //   };
  // }, [sampler]);

  const [sampler, setSampler] = React.useState<Tone.Sampler | undefined>(() =>
    isCapacitorNative() ? undefined : new Tone.Sampler({ urls: {}, release: 1 }).toDestination(),
  );

  const samplerRef = React.useRef<Tone.Sampler | undefined>(sampler);
  React.useEffect(() => {
    samplerRef.current = sampler;
  }, [sampler]);

  const loadingRef = React.useRef<Promise<void> | null>(null);
  const samplesLoadedRef = React.useRef(false);

  React.useEffect(() => {
    (window as MuseFlowAudioWindow).__ensureSamplerReady = async (forceRebuild = false) => {
      if (loadingRef.current) return loadingRef.current;

      loadingRef.current = (async () => {
        await Tone.start();
        let raw = Tone.getContext().rawContext as AudioContext;

        const needsRecreation = (ctx: AudioContext | BaseAudioContext | undefined) => {
          const state = ctx?.state;
          return !ctx || state === 'closed' || state === 'interrupted';
        };

        if (needsRecreation(raw)) {
          console.log('[ensureSamplerReady] Context in bad state, recreating...', raw?.state);
          try {
            Tone.getContext().dispose();
          } catch {
            // intentionally empty
          }
          const newCtx = new Tone.Context({ latencyHint: 'interactive' });
          Tone.setContext(newCtx);
          await Tone.start();
          raw = Tone.getContext().rawContext as AudioContext;
        }

        if (raw.state !== 'running') {
          try {
            await raw.resume();
          } catch {
            // intentionally empty
          }
          await new Promise((resolve) => setTimeout(resolve, 50));
        }

        const currentRaw = Tone.getContext().rawContext as AudioContext;
        const existing = samplerRef.current;
        const samplerRaw = existing?.context?.rawContext as AudioContext | undefined;

        const ctxMismatch = !!samplerRaw && samplerRaw !== currentRaw;
        const missingSampler = !existing;
        const mustRebuild = forceRebuild || ctxMismatch;

        if (mustRebuild || missingSampler) {
          try {
            samplerRef.current?.dispose();
          } catch {
            // intentionally empty
          }
          samplesLoadedRef.current = false;

          const next = new Tone.Sampler({ urls: {}, release: 1 }).toDestination();
          samplerRef.current = next;
          setSampler(next);
          (window as MuseFlowAudioWindow).__museflowSampler = next;
        } else {
          (window as MuseFlowAudioWindow).__museflowSampler = samplerRef.current;
        }

        if (!samplesLoadedRef.current && samplerRef.current) {
          const { SALAMANDER_B64 } = await import('assets/audio/salamander.base64');
          const s = samplerRef.current;
          for (const [note, b64] of Object.entries(SALAMANDER_B64)) {
            try {
              const buf = await decodeBase64ToAudioBuffer(currentRaw, b64);
              s.add(note, buf);
            } catch (err) {
              reportError(err, 'audio.sampler');
              throw err;
            }
          }
          samplesLoadedRef.current = true;
        }

        (window as MuseFlowAudioWindow).__audioUnlocked = true;
        window.dispatchEvent(new CustomEvent('AudioReady', { detail: { ready: true } }));
      })();

      try {
        await loadingRef.current;
      } finally {
        loadingRef.current = null;
      }
    };

    return () => {
      delete (window as MuseFlowAudioWindow).__ensureSamplerReady;
    };
  }, []);

  // const sampler = React.useMemo(() => {
  //   return IS_NATIVE
  //     ? new Tone.Sampler({ urls: {}, release: 1 }).toDestination() // native: we will .add() buffers
  //     : new Tone.Sampler(TONE_SAMPLES).toDestination();            // web: URL-based samples
  // }, []);

  // React.useEffect(() => {
  //   samplerRef.current = new Tone.Sampler({ urls: {}, release: 1 }).toDestination();
  //   return () => {
  //     samplerRef.current?.dispose?.();
  //     samplerRef.current = null;
  //   };
  // }, []);

  // React.useEffect(() => {
  //   let cancelled = false;
  //   (async () => {
  //     try {
  //       await Tone.start(); // unlock context once
  //       const ctx = Tone.getContext().rawContext;
  //       // decode and add all notes
  //       for (const [note, b64] of Object.entries(SALAMANDER_B64)) {
  //         // fixme
  //         const buf = await decodeBase64ToAudioBuffer(ctx as any, b64);
  //         sampler.add(note as any, buf); // <-- no fetch/XHR involved
  //       }
  //       await sampler.loaded;
  //       // if (!cancelled) setSamplerReady(true);
  //     } catch (e) {
  //       console.error('Sampler load failed', e);
  //     }
  //   })();
  //   return () => { cancelled = true; };
  // }, [sampler]);

  // Keep app-data startup separate from analytics/visit recording. The app-data
  // fetch must run once whenever auth becomes true, even if storage rehydration is slow.
  const [hasStartedAuthenticatedApp, setHasStartedAuthenticatedApp] = useState(false);
  const [hasRecordedVisit, setHasRecordedVisit] = useState(false);
  const [_screenOrientationSet, setScreenOrientationSet] = useState(false);

  const [renderRepertoirePlay, setRenderRepertoirePlay] = useState(true); // This will switch false & true to re-redner components to "refresh"
  const showHistoryOfPianoMusic = useFeatureFlag(FeatureFlag.USE_HISTORY_OF_PIANO_MUSIC);

  const data = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const { sessionId } = useSelector((state: EventReducer) => state.eventReducer);
  const [_playToneSound, _SetplayToneSound] = useState(true);

  function _getOperatingSystemBackup() {
    let operatingSystem = 'Not known';
    if (window.navigator.appVersion.indexOf('Win') !== -1) {
      operatingSystem = 'Windows OS';
    }
    if (window.navigator.appVersion.indexOf('Mac') !== -1) {
      operatingSystem = 'MacOS';
    }
    if (window.navigator.appVersion.indexOf('X11') !== -1) {
      operatingSystem = 'UNIX OS';
    }
    if (window.navigator.appVersion.indexOf('Linux') !== -1) {
      operatingSystem = 'Linux OS';
    }

    return operatingSystem;
  }

  useEffect(() => {
    CapApp.addListener('appUrlOpen', async ({ url }) => {
      if (!url?.startsWith('museflow://auth-callback')) return;
      try {
        await oidcManager.signinRedirectCallback(url);
        navigate('/', { replace: true });
        // tokens are now stored, auth.isAuthenticated becomes true
        try {
          await Browser.close();
        } catch {
          // intentionally empty
        }
      } catch (err) {
        console.error('OIDC redirect handling failed:', err);
      }
    });
  }, [navigate]);

  useEffect(() => {
    const autoplayClickHandler = () => dispatch(appActions.setInteractedWithPage(true));
    window.addEventListener('mousedown', autoplayClickHandler);
    return () => {
      window.removeEventListener('mousedown', autoplayClickHandler);
    };
  }, [dispatch]);

  const isFirstSignIn = data.userDataFetched && data.userData?.subscription_status === '';

  useEffect(() => {
    const isOnPreAuthPage = location.pathname.includes('signin');
    if (auth.isAuthenticated && data.userDataFetched) {
      if (isFirstSignIn && location.pathname !== '/welcome') {
        navigate('/welcome', { replace: true });
      } else if (!isFirstSignIn && isOnPreAuthPage) {
        navigate('/roadmap', { replace: true });
      }
    }
  }, [auth.isAuthenticated, location.pathname, isFirstSignIn, data.userDataFetched, navigate]);

  useEffect(() => {
    if (
      location.pathname == '/tutorial' ||
      location.pathname == '/lesson' ||
      location.pathname == '/roadmap'
    ) {
      dispatch(lessonActions.setTierSummaryRequestError(false));
      dispatch(lessonActions.updatePhrasesTilNextTier(MIN_NUM_PHRASES));
      dispatch(lessonActions.setTierSummary(null));
      dispatch(lessonActions.setLessonProcessPending(false));
    }
  }, [location.pathname]);
  const dispatchInterval = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopDispatchInterval = React.useCallback(() => {
    if (dispatchInterval.current) {
      clearInterval(dispatchInterval.current);
      dispatchInterval.current = null;
    }
  }, []);

  const startDispatchInterval = React.useCallback(() => {
    stopDispatchInterval();
    dispatchInterval.current = setInterval(() => {
      // Use the singleton directly. Do NOT also dispatch(dispatchEvents()) — the saga
      // would call eventQueue.dispatchEvents() again, causing duplicate HTTP requests.
      eventQueue
        .dispatchEvents()
        .catch((err) => logger.warn('event dispatch interval failed', err));
    }, eventIntervalSecondsInt * 1000);
  }, [eventIntervalSecondsInt, stopDispatchInterval]);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      stopDispatchInterval();
      return;
    }

    startDispatchInterval();

    let appStateSub: PluginListenerHandle | undefined;
    CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        startDispatchInterval();
        eventQueue.dispatchEvents().catch(() => {});
      } else {
        stopDispatchInterval();
        eventQueue.dispatchEvents().catch(() => {});
      }
    })
      .then((handle) => {
        appStateSub = handle;
      })
      .catch(() => {});

    const onVisibilityChange = () => {
      if (document.hidden) {
        stopDispatchInterval();
      } else {
        startDispatchInterval();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopDispatchInterval();
      appStateSub?.remove?.();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [auth.isAuthenticated, startDispatchInterval, stopDispatchInterval]);

  useEffect(() => {
    if (auth.isAuthenticated && data.userData) {
      // only call once userData row has been created & is present
      dispatch(appActions.getSubscriptionStatus());
    }
    if (!auth.isAuthenticated) {
      setHasStartedAuthenticatedApp(false);
      return;
    }

    if (!hasStartedAuthenticatedApp) {
      logger.info(`
 __  __                    _____  _                 
|  \\/  | _   _  ___   ___ |  ___|| |  ___ __      __
| |\\/| || | | |/ __| / _ \\| |_   | | / _ \\ \\ /\\ / /
| |  | || |_| |\\__ \\|  __/|  _|  | || (_) |\\ V  V /  111111
|_|  |_| \\__,_||___/ \\___||_|    |_| \\___/  \\_/\\_/  
      `);
      dispatch(appActions.authenticatedStartup());
      setHasStartedAuthenticatedApp(true);
    }
  }, [auth.isAuthenticated, hasStartedAuthenticatedApp]);

  useEffect(() => {
    if (auth.user && sessionId && auth?.user?.attributes?.sub) {
      console.log('adding session attributes');
      awsRum?.addSessionAttributes({
        museflowUserId: auth?.user?.attributes?.sub,
        analyticsSessionId: sessionId,
        // Add device/browser info for easier filtering (also in metadata by default)
        userBrowser: browser?.name || 'unknown',
        userBrowserVersion: browser?.version || 'unknown',
        userOS: browser?.os || 'unknown',
        userDeviceType: detectDevice(),
      });
    }

    if (auth.user && sessionId && !hasRecordedVisit) {
      dispatch(eventActions.visitEventAction());
      // we only want this to fire when the page loads, but we need to wait
      // until the user data is loaded and the session id is set.
      setHasRecordedVisit(true);
    }
  }, [auth.user, sessionId, hasRecordedVisit, data.userData?.user_id]);

  useEffect(() => {
    const previousLocation = sessionStorage.getItem('previousLocation');
    // in case of refresh; don't set unless changed
    if (previousLocation != location.pathname) {
      sessionStorage.setItem('previousLocation', location.pathname);
    }

    if (process.env.REACT_APP_NODE_ENV == 'prd') {
      const gtag_tracking_id = 'AW-11476609062';
      // Initialize Google Tracking Tag / Analytics (only once)
      if (!window.dataLayer) {
        window.dataLayer = [];
        window.gtag = function (...args: unknown[]) {
          window.dataLayer.push(args);
        };
        window.gtag('js', new Date());
        window.gtag('config', gtag_tracking_id);
      }
      // Track page views:
      const pagePath = location.pathname + location.search;
      window.gtag('config', gtag_tracking_id, { page_path: pagePath });
      awsRum?.recordPageView(location.pathname);
      ReactGA?.send({ hitType: 'pageview', page: location.pathname });
    }
  }, [location]);

  // Emit an internal PageViewEvent on each authenticated navigation. The
  // gtag/RUM tracking above is prod-only and external; this populates our own
  // event store (page_view_event) in every environment. Gated like the visit
  // event on a loaded user + analytics session.
  useEffect(() => {
    if (auth.user && sessionId) {
      dispatch(eventActions.pageViewEventAction(location.pathname));
    }
  }, [auth.user, sessionId, location.pathname, dispatch]);

  // Keep the OS status bar visible but stop the webview from rendering behind
  // it. Without this, `viewport-fit=cover` lets our fixed/full-bleed layouts
  // (e.g. the repertoire header) slide under the status bar and clip. Laying the
  // webview out below the bar gives us a normal app-style viewport so we don't
  // have to compensate with per-element safe-area padding.
  useEffect(() => {
    if (!isCapacitorNative()) return;
    (async () => {
      try {
        await StatusBar.setOverlaysWebView({ overlay: false });
        await StatusBar.setStyle({ style: Style.Light });
      } catch (err) {
        console.warn('StatusBar configuration error', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isCapacitorNative() || Capacitor.getPlatform() !== 'ios') return;
    if (!data.userData || !currentAuthToken) return;
    // At this point, you can call your setup function or directly use window.store.
    try {
      setupIAP(data.userData, dispatch, currentAuthToken);
    } catch (err) {
      console.error('IAP init failed:', err);
    }
  }, [data.userData, dispatch, currentAuthToken]);

  useEffect(() => {
    if (!isMuseFlowAudioPluginAvailable()) return;
    if (!auth.isAuthenticated || !isNativeAudioRoute(location.pathname)) return;

    const isTutorial = location.pathname.startsWith('/tutorial');
    console.log(
      `[EXERCISE-DBG] App.tsx audio init effect — path=${location.pathname} isTutorial=${isTutorial} calling=${isTutorial ? 'initialize' : 'warmUp'}`,
    );
    const audioCall = isTutorial ? MuseFlowAudio.initialize() : MuseFlowAudio.warmUp();

    audioCall
      .then(() => {
        console.log(`[EXERCISE-DBG] App.tsx ${isTutorial ? 'initialize' : 'warmUp'}() resolved OK`);
      })
      .catch((err: unknown) => {
        console.error(
          `[EXERCISE-DBG] App.tsx ${isTutorial ? 'initialize' : 'warmUp'}() FAILED:`,
          err,
        );
        reportError(err, 'audio.nativeInit');
      });

    return () => {};
  }, [auth.isAuthenticated, location.pathname]);

  useEffect(() => {
    if (!isMuseFlowAudioPluginAvailable()) return;

    let cancelled = false;
    let interruptedHandle: PluginListenerHandle | undefined;
    let endedHandle: PluginListenerHandle | undefined;

    const registerListeners = async () => {
      interruptedHandle = await MuseFlowAudio.addListener('interrupted', () => {
        console.log(
          '[App.tsx] received native "interrupted" event — dispatching NativeAudioInterrupted',
        );
        window.dispatchEvent(new CustomEvent('NativeAudioInterrupted'));
      });
      if (cancelled) {
        interruptedHandle.remove();
        interruptedHandle = undefined;
        return;
      }
      endedHandle = await MuseFlowAudio.addListener(
        'interruptionEnded',
        (eventData: { shouldResume: boolean }) => {
          console.log(
            '[App.tsx] received native "interruptionEnded" event, shouldResume=',
            eventData.shouldResume,
          );
          if (eventData.shouldResume) {
            window.dispatchEvent(new CustomEvent('NativeAudioResumed'));
          }
        },
      );
      if (cancelled) {
        endedHandle.remove();
        endedHandle = undefined;
      }
    };

    registerListeners();

    return () => {
      cancelled = true;
      interruptedHandle?.remove();
      endedHandle?.remove();
    };
  }, []);

  useEffect(() => {
    dispatch(appActions.setInteractedWithPage(false));
  }, []);

  useEffect(() => {
    syncResponsiveDataAttributes(location.pathname, getDeviceClass());
  }, [location.pathname]);

  useEffect(() => {
    if (!isCapacitorNative()) {
      setScreenOrientationSet(true);
      return;
    }

    (async () => {
      try {
        const markReady = () => {
          setTimeout(() => {
            setScreenOrientationSet(true);
          }, 150);
        };

        // Only lock orientation on iPad-sized-or-smaller screens; leave larger
        // devices (laptops/desktops running natively) unrestricted.
        if (!isOrientationLockEligibleScreen()) {
          await ScreenOrientation.unlock();
          markReady();
          return;
        }

        // Lock every route to landscape except the exempt routes (repertoire play),
        // which are left free to rotate — but only on tablet-sized screens. On
        // phones even those routes stay locked to landscape.
        const orientationMode = getOrientationLockForRoute(
          location.pathname,
          isFreeRotationEligibleScreen(),
        );
        if (orientationMode === 'landscape') {
          await ScreenOrientation.lock({ orientation: 'landscape' });
        } else if (orientationMode === 'portrait') {
          await ScreenOrientation.lock({ orientation: 'portrait' });
        } else {
          await ScreenOrientation.unlock();
        }
        markReady();
      } catch (err) {
        console.warn('Orientation lock error', err);
      }
    })();
  }, [location.pathname]);

  // Show loading screen while waiting for user data, or while a redirect is pending.
  // This prevents the sign-in page or other routes from flashing before navigation completes.
  const isOnPreAuthPage = location.pathname.includes('signin');
  const needsRedirect =
    auth.isAuthenticated &&
    data.userDataFetched &&
    ((isFirstSignIn && location.pathname !== '/welcome') || (!isFirstSignIn && isOnPreAuthPage));

  if (auth.isAuthenticated && (!data.userDataFetched || needsRedirect)) {
    console.warn(
      '[MUS-61] Showing loading screen.',
      'userDataFetched:',
      data.userDataFetched,
      'needsRedirect:',
      needsRedirect,
    );
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CustomCircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <MidiProvider location={location} navigate={navigate} sampler={sampler}>
      <Routes>
        <Route path="/register" element={<Signup />} />
        <Route path="/signin" element={<Signin />} />
        <Route path="/verify" element={<Verification />} />
        {/* <Route path='/forgot' element={<Forgot/>} /> */}
        {/*  Make sure to use these two (looks pretty good already) */}
        <Route
          path="/plan"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <Plan />{' '}
            </ProtectedRoute>
          }
        />
        {/* <Route path='/payment' element={<ProtectedRoute redirectPath='/signin'> <Payment/> </ProtectedRoute>} /> */}
        <Route
          path="/welcome"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <Welcome />{' '}
            </ProtectedRoute>
          }
        />
        <Route
          path="/sound-output"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <SoundOutput />{' '}
            </ProtectedRoute>
          }
        />
        <Route
          path="/roadmap"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <Roadmap />{' '}
            </ProtectedRoute>
          }
        />
        <Route
          path="/repertoire"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <Repertoire />{' '}
            </ProtectedRoute>
          }
        />
        {/* <Route path='/home' element={<ProtectedRoute redirectPath='/signin'> <Home/> </ProtectedRoute>} /> */}
        {/* <Route path='/browse' element={<ProtectedRoute redirectPath='/signin'> <Browse/> </ProtectedRoute>} /> */}
        <Route
          path="/repertoire-play"
          element={
            <ProtectedRoute redirectPath="/signin">
              {renderRepertoirePlay && (
                <RepertoirePlayProvider>
                  <RepertoirePlay
                    renderRepertoirePlay={renderRepertoirePlay}
                    setRenderRepertoirePlay={setRenderRepertoirePlay}
                  />
                </RepertoirePlayProvider>
              )}
            </ProtectedRoute>
          }
        />
        {/* Still allowing the FAQ route, but will redirect to https://museflow.ai/faq */}
        <Route
          path="/faq"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <Help />{' '}
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <Settings />{' '}
            </ProtectedRoute>
          }
        />
        {/* <Route path='/leveldesscription' element={<ProtectedRoute redirectPath='/signin'> <LevelDescription/> </ProtectedRoute>} /> */}
        <Route
          path="/exercises/note-length"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <NoteLength
                notes={[NoteType.QUARTER, NoteType.QUARTER, NoteType.QUARTER, NoteType.QUARTER]}
                bpm={72}
              />{' '}
            </ProtectedRoute>
          }
        />
        <Route
          path="/tutorial"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <Tutorial />{' '}
            </ProtectedRoute>
          }
        />
        <Route
          path="/introduction"
          element={
            <ProtectedRoute redirectPath="/signin">
              {' '}
              <Introduction />{' '}
            </ProtectedRoute>
          }
        />
        <Route
          path="/lesson"
          element={
            <ProtectedRoute redirectPath="/signin">
              <Lesson />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute redirectPath="/signin">
              <Dashboard />
            </ProtectedRoute>
          }
        />
        {showHistoryOfPianoMusic && (
          <Route
            path="/history-of-piano-music"
            element={
              <ProtectedRoute redirectPath="/signin">
                <HistoryOfPianoMusic />
              </ProtectedRoute>
            }
          />
        )}
        <Route path="/404" element={<ErrorPage />} />
        <Route path="/" element={<StartupRouteResolver />} />
        {/* Navigate homepage to roadmap */}
        <Route path="/*" element={<Navigate to="/404" />} />
      </Routes>
    </MidiProvider>
  );
};

//const RootApp: React.FC<AppProps> = ({ store }) => {
const RootApp: React.FC = () => {
  return (
    <div className="App">
      <AuthWrapperProvider>
        <BrowserRouter>
          {!isCapacitorNative() && <AudioUnlocker />}
          <OfflineModal />
          <DeviceCompatibilityBlockingScreen />
          <AnnouncementModalHost />
          {process.env.REACT_APP_MAINTENANCE == 'true' && <MaintenancePage />}
          {process.env.REACT_APP_MAINTENANCE != 'true' && <App />}
        </BrowserRouter>
      </AuthWrapperProvider>
    </div>
  );
};

interface ErrorBoundaryProps {
  store: Store<RootState>;
}

// class ErrorBoundary extends React.Component<{}, {hasError: boolean}> {
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- ErrorBoundary kept for future use
class ErrorBoundary extends React.Component<ErrorBoundaryProps, { hasError: boolean }> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // You can also log the error to an error reporting service
    awsRum?.recordError(error);
    void errorInfo; // reserve for logging
  }
  render() {
    console.log('[ErrorBoundary] render');
    // No need to present error page on every uncaught error
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return <ErrorPage />;
    } else {
      return <RootApp />;
    }
  }
}

export default RootApp;
